import dayjs, { ManipulateType } from "dayjs";

import { IUserPreferences } from "../@types/Preferences";
import { IPatientTreatment } from "../@types/Treatments";
import { AUTH_UNIT_PREFERENCES } from "../constants/localStorageKeys";
import {
  DATE_FILTER_ALL,
  DateFilterOptionType,
} from "../pages/PatientPage/components/MeasurementModal/components/CustomOptionsBar";
import { safeStorage } from "./storage";

const HOUR = 60 * 60 * 1000;

type GraphSeparatorResult = {
  graphSeparators: number[];
  dates: number[];
  separatorCounter: number;
  tickFormat: string;
  viewType: string;
  isTickMultipleLine: boolean;
  isSoloDataPoint: boolean;
  interval: "24H" | "48H" | "7D" | "1M" | "2M" | "6M" | "1Y" | "4Y" | "4Y+";
};

export type NebulizerChartRow = IPatientTreatment & {
  timeValue: number;
  totalTreatmentTimeNum: number;
  avgInhalingTimeNum?: number;
  treatmentsCount?: number;
  isAverage?: boolean;
  monthRangeLabel?: string;
  latestTreatmentTime?: number;
};

const monthStartTs = (ts: number) => {
  const d = new Date(ts);
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};
const nextMonthStartTs = (ms: number) => {
  const d = new Date(ms);
  d.setMonth(d.getMonth() + 1);
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

const startOfDayTs = (ts: number) => {
  const d = new Date(ts);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

const nextDayStartTs = (dayStart: number) => {
  const d = new Date(dayStart);
  d.setDate(d.getDate() + 1); // moves to next calendar day (handles DST)
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

export function getGraphSeparators(
  dateInterval: DateFilterOptionType | undefined,
  minDate: dayjs.Dayjs | undefined,
  maxDate: dayjs.Dayjs | undefined
): GraphSeparatorResult {
  const graphSeparators: number[] = [];
  const dates: number[] = [];

  let separatorCounter = 0;
  let tickFormat = "";
  let viewType = "";
  let isTickMultipleLine = false;
  let interval:
    | "24H"
    | "48H"
    | "7D"
    | "1M"
    | "2M"
    | "6M"
    | "1Y"
    | "4Y"
    | "4Y+" = "24H";
  const isSoloDataPoint = minDate?.isSame(maxDate) ?? false;

  if (!dateInterval) {
    return {
      graphSeparators,
      dates,
      separatorCounter,
      tickFormat,
      viewType,
      isTickMultipleLine,
      isSoloDataPoint,
      interval,
    };
  }

  let currentDate = dateInterval.startDate;
  let { endDate } = dateInterval;

  if (currentDate.isSame(endDate, "day")) {
    endDate = endDate.add(1, "day").add(-1, "millisecond");
  }

  if (minDate && maxDate && dateInterval.id === DATE_FILTER_ALL) {
    const totalRangeMs = maxDate.valueOf() - minDate.valueOf();
    const isSameDay = minDate.isSame(maxDate, "day");
    if (!isSoloDataPoint && !isSameDay) {
      const gap = totalRangeMs * 0.1;
      currentDate = dayjs(minDate.valueOf() - gap);
      endDate = dayjs(maxDate.valueOf() + gap);
    } else {
      currentDate = minDate.startOf("day");
      endDate = minDate.add(1, "day").startOf("day");
    }
  }

  if (!minDate && !maxDate) {
    currentDate = dayjs();
    endDate = endDate.subtract(1, "year");
  }

  const tickInterval = endDate.valueOf() - currentDate.valueOf(); // millisecond difference

  let incrementValue = 1;
  let unitValue: ManipulateType = "days";
  let startsAtMonday = false;

  switch (true) {
    case tickInterval <= 86400000: // 24 hours
      incrementValue = 30;
      unitValue = "minutes";
      separatorCounter = 4;
      tickFormat = "MMM D, HH:mm";
      isTickMultipleLine = true;
      interval = "24H";
      break;
    case tickInterval > 86400000 && tickInterval <= 172800000: // 48 hours
      incrementValue = 3;
      unitValue = "hours";
      separatorCounter = 4;
      tickFormat = "MMM D, HH:mm";
      isTickMultipleLine = true;
      interval = "48H";
      break;
    case tickInterval > 172800000 && tickInterval <= 604800000: // 7 days
      incrementValue = 12;
      unitValue = "hours";
      separatorCounter = 2;
      tickFormat = "MMM D";
      interval = "7D";
      break;
    case tickInterval > 604800000 && tickInterval <= 5270400000: // 2 months
      incrementValue = 2;
      unitValue = "days";
      separatorCounter = 2;
      tickFormat = "MMM D";
      interval = "2M";
      break;
    case tickInterval > 5270400000 && tickInterval <= 15724800000: // 6 months
      unitValue = "week";
      tickFormat = "MMM";
      startsAtMonday = true;
      viewType = "monthly";
      interval = "6M";
      break;
    case tickInterval > 15724800000 && tickInterval <= 15924800000: // 6 months to give nebulizer bars the corerct thickness
      unitValue = "month";
      tickFormat = "MMM";
      viewType = "yearly";
      interval = "6M";
      break;
    case tickInterval > 15924800000 && tickInterval <= 31537000000: // 1 year
      unitValue = "month";
      tickFormat = "MMM";
      viewType = "yearly";
      interval = "1Y";
      break;
    case tickInterval > 31536000000 && tickInterval <= 126144000000: // Greater than 1 year, less than 4 years
      unitValue = "month";
      tickFormat = "Q";
      viewType = "yearly";
      interval = "4Y";
      break;
    case tickInterval > 126144000000: // Greater than 4 years
      unitValue = "year";
      tickFormat = "YYYY";
      viewType = "multiYear";
      interval = "4Y+";
      break;
    default:
      break;
  }

  if (viewType === "monthly" || viewType === "yearly") {
    let dateToAdd = currentDate;
    if (dateInterval.id === DATE_FILTER_ALL) {
      graphSeparators.push(currentDate.startOf("month").valueOf());
    }

    while (dateToAdd <= endDate) {
      if (tickFormat !== "Q") {
        dateToAdd = dateToAdd.add(1, "month").startOf("month");
        dates.push(dateToAdd.valueOf());
        graphSeparators.push(dateToAdd.valueOf());
      } else {
        dateToAdd = dateToAdd.startOf("month");

        if (
          dateToAdd.month() !== 0 &&
          dateToAdd.month() !== 3 &&
          dateToAdd.month() !== 6 &&
          dateToAdd.month() !== 9
        ) {
          dateToAdd = dateToAdd.subtract(1, "month").startOf("month");
        } else {
          graphSeparators.push(dateToAdd.valueOf());
          dateToAdd = dateToAdd.add(3, "month").startOf("month");
        }
      }
    }

    dates.pop();
  }

  if (viewType === "multiYear") {
    let dateToAdd = currentDate;

    while (dateToAdd <= endDate) {
      dateToAdd = dateToAdd.add(1, "year").startOf("year");
      dates.push(dateToAdd.valueOf());
      graphSeparators.push(dateToAdd.valueOf());
    }

    dates.pop();
  }

  while (currentDate <= endDate && viewType !== "yearly") {
    if (startsAtMonday) {
      if (currentDate.day() !== 1) {
        const daysUntilMonday = 8 - currentDate.day();
        currentDate = dayjs(currentDate).add(daysUntilMonday, "days");
      }
      startsAtMonday = false;
    }

    dates.push(currentDate.valueOf());

    currentDate = dayjs(currentDate).add(incrementValue, unitValue);
  }

  dates.push(currentDate.valueOf());
  dates.sort((a, b) => a - b);

  return {
    graphSeparators,
    dates,
    separatorCounter,
    tickFormat,
    viewType,
    isTickMultipleLine,
    isSoloDataPoint,
    interval,
  };
}

export type EmptyAllAxis = {
  anchor: dayjs.Dayjs;
  domain: [number, number];
};

/**
 * When the "ALL" filter is selected but there is no data, the underlying date
 * range spans 1900 -> now — feeding that into `getGraphSeparators` produces
 * ~150 yearly ticks that overlap on the X axis. This helper returns an anchor
 * (the filter's endDate) and a 1-day domain to clamp the axis to, or `null`
 * when the override doesn't apply.
 *
 * Usage in a bar chart:
 *   const emptyAll = getEmptyAllAxis(dateInterval, isEmpty);
 *   getGraphSeparators(
 *     dateInterval,
 *     emptyAll?.anchor ?? (isEmpty ? dateInterval.startDate : minDate),
 *     emptyAll?.anchor ?? (isEmpty ? dateInterval.endDate : maxDate),
 *   );
 *   // in handleXDomain:
 *   if (emptyAll) return emptyAll.domain;
 */
export function getEmptyAllAxis(
  dateInterval: DateFilterOptionType | undefined,
  isEmpty: boolean | undefined
): EmptyAllAxis | null {
  if (!dateInterval || !isEmpty || dateInterval.id !== DATE_FILTER_ALL) {
    return null;
  }
  const dayStart = dateInterval.endDate.startOf("day");
  return {
    anchor: dateInterval.endDate,
    domain: [dayStart.valueOf(), dayStart.add(1, "day").valueOf()],
  };
}

export function formatMeasurementAverageRangeDates(
  startDate: dayjs.Dayjs,
  endDate: dayjs.Dayjs
) {
  const start = dayjs(startDate);
  const end = dayjs(endDate);

  if (!start.isValid() || !end.isValid()) {
    return "";
  }

  const sameYear = start.year() === end.year();
  const sameMonth = sameYear && start.month() === end.month();
  const sameDay = sameMonth && start.date() === end.date();

  if (!sameYear) {
    return `${start.format("MMM DD, YYYY")} - ${end.format("MMM DD, YYYY")}`;
  }

  if (sameDay) {
    return `${start.format("MMM DD, YYYY")}`;
  }

  if (sameMonth) {
    return `${start.format("MMM DD")} - ${end.format("DD, YYYY")}`;
  }

  return `${start.format("MMM DD")} - ${end.format("MMM DD, YYYY")}`;
}

export const getTrendLineDisplaySettingFromLocalStorage = (
  measurementType: string
) => {
  const userUnitPreferences = safeStorage.getItem(AUTH_UNIT_PREFERENCES);
  if (!userUnitPreferences) return false;

  const parsedUnitPreferences = JSON.parse(
    userUnitPreferences
  ) as IUserPreferences;

  try {
    const trendLinePreferences = JSON.parse(
      parsedUnitPreferences.trendLineDisplayPreferences
    );
    if (
      trendLinePreferences &&
      typeof trendLinePreferences[measurementType] === "boolean"
    ) {
      return trendLinePreferences[measurementType];
    }
  } catch {
    return false;
  }

  return false;
};

// Reads the data-display preference (e.g. "BestOfDay" / "Highest of day") for a
// measurement directly from localStorage. Login writes preferences straight to
// localStorage without refreshing the UserPreferences context, so reading from
// localStorage is the only way to get the up-to-date value on the first render
// after logging in. The value is stored per spirometry type, falling back to
// the shared `default` for everything else.
export const getDataDisplayPreferenceFromLocalStorage = (
  measurementType: string
): string => {
  const userUnitPreferences = safeStorage.getItem(AUTH_UNIT_PREFERENCES);
  if (!userUnitPreferences) return "";

  try {
    const parsedUnitPreferences = JSON.parse(
      userUnitPreferences
    ) as IUserPreferences;

    const raw = parsedUnitPreferences.dataDisplayPreference;
    if (!raw) return "";

    const dataPreferences = typeof raw === "string" ? JSON.parse(raw) : raw;

    if (typeof dataPreferences !== "object" || dataPreferences === null) {
      return "";
    }

    return dataPreferences[measurementType] || dataPreferences.default || "";
  } catch {
    return "";
  }
};

export function getLinearRegressionValues(
  xValues: number[],
  yValues: number[]
) {
  const n = xValues.length;

  if (xValues.length !== yValues.length) {
    return { slope: 0, intercept: 0 };
  }

  const sumX = xValues.reduce((sum, x) => sum + x, 0);
  const sumY = yValues.reduce((sum, y) => sum + y, 0);
  const sumXY = xValues.reduce((sum, x, i) => sum + x * yValues[i], 0);
  const sumXX = xValues.reduce((sum, x) => sum + x * x, 0);

  const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;

  return { slope, intercept };
}

export const calculateLinearRegression = (
  xValues: number[],
  yValues: number[]
) => {
  // Return empty array if no data to avoid NaN values in chart
  if (!xValues.length || !yValues.length) {
    return [];
  }

  const { slope, intercept } = getLinearRegressionValues(xValues, yValues);

  // Check for NaN values from the regression calculation
  if (!Number.isFinite(slope) || !Number.isFinite(intercept)) {
    return [];
  }

  const xStart = xValues[0];
  const xEnd = xValues[xValues.length - 1];

  const yStart = slope * xStart + intercept;
  const yEnd = slope * xEnd + intercept;

  // Validate the calculated y values
  if (!Number.isFinite(yStart) || !Number.isFinite(yEnd)) {
    return [];
  }

  return [
    { x: xStart, y: yStart },
    { x: xEnd, y: yEnd },
  ];
};

export function centerPerDay<
  T extends { timeValue: number; totalTreatmentTimeNum: number }
>(items: T[], interval: "7D" | "1M" | "2M"): T[] {
  const byDay = new Map<number, T[]>();
  items.forEach((it) => {
    const key = startOfDayTs(it.timeValue);
    const list = byDay.get(key) ?? [];
    byDay.set(key, [...list, it]);
  });

  const result = Array.from(byDay.entries())
    .sort(([a], [b]) => a - b)
    .flatMap(([dayStart, list]) => {
      const nextStart = nextDayStartTs(dayStart);
      const daySpan = nextStart - dayStart;
      const dayMid = dayStart + (interval === "7D" ? 12 : 12) * HOUR;
      const sorted = [...list].sort((a, b) => a.timeValue - b.timeValue);
      const n = sorted.length;
      if (n === 1) return [{ ...sorted[0], timeValue: dayMid }];

      let spacing = 6 * HOUR;
      const maxIndexDistance = n % 2 === 1 ? Math.floor(n / 2) : n / 2;

      const maxAllowedHalf = daySpan / 2;
      const maxNeededHalf = maxIndexDistance * spacing;

      if (maxIndexDistance > 0 && maxNeededHalf > maxAllowedHalf) {
        spacing = maxAllowedHalf / maxIndexDistance;
      }

      const offsets = sorted.map((_it, i) => {
        if (n % 2 === 1) {
          const mid = Math.floor(n / 2);
          return (i - mid) * spacing;
        }
        const leftCenter = n / 2 - 1;
        return (i - leftCenter - 0.5) * spacing;
      });

      return sorted.map((it, i) => {
        const pos = Math.max(
          dayStart,
          Math.min(nextStart, dayMid + offsets[i])
        );
        return { ...it, timeValue: pos };
      });
    });

  return result;
}

export function buildMonthlyAvg(
  items: IPatientTreatment[],
  domainStartMs: number,
  domainEndMs: number,
  daysOffset: number
): NebulizerChartRow[] {
  if (!items?.length) return [];

  const round1 = (num: number) => Math.round(num * 10) / 10;

  const start = monthStartTs(domainStartMs);
  const end = nextMonthStartTs(monthStartTs(domainEndMs));

  const base = items
    .map((t) => {
      const tv = new Date(t.time).getTime();
      return {
        monthKey: monthStartTs(tv),
        timeValue: tv,
        value: Number.isFinite(+t.totalTreatmentTime)
          ? +t.totalTreatmentTime
          : 0,
        inhValue: Number.isFinite(+t.totalTimeInhaling)
          ? +t.totalTimeInhaling
          : 0,
        complete: t.doseStatus === 1,
        original: t,
      };
    })
    .filter((r) => r.monthKey >= start && r.monthKey < end);

  // aggregate per month
  const byMonth = new Map<
    number,
    {
      cSum: number;
      cN: number;
      cLatest: number;
      iSum: number;
      iN: number;
      iLatest: number;
      cInhSum: number;
      iInhSum: number;
    }
  >();

  base.forEach((r) => {
    const prev = byMonth.get(r.monthKey) ?? {
      cSum: 0,
      cN: 0,
      cLatest: 0,
      iSum: 0,
      iN: 0,
      iLatest: 0,
      cInhSum: 0,
      iInhSum: 0,
    };

    if (r.complete) {
      byMonth.set(r.monthKey, {
        ...prev,
        cSum: prev.cSum + r.value,
        cInhSum: prev.cInhSum + r.inhValue,
        cN: prev.cN + 1,
        cLatest: r.timeValue > prev.cLatest ? r.timeValue : prev.cLatest,
      });
    } else {
      byMonth.set(r.monthKey, {
        ...prev,
        iSum: prev.iSum + r.value,
        iInhSum: prev.iInhSum + r.inhValue,
        iN: prev.iN + 1,
        iLatest: r.timeValue > prev.iLatest ? r.timeValue : prev.iLatest,
      });
    }
  });

  const rows: NebulizerChartRow[] = Array.from(byMonth.entries())
    .sort(([a], [b]) => a - b)
    .flatMap(([monthTs, agg]) => {
      const next = nextMonthStartTs(monthTs);
      const boundary = monthTs;
      const offsetMs = daysOffset * 24 * HOUR;

      const leftPos = Math.max(domainStartMs, boundary - offsetMs);
      const rightPos = Math.min(domainEndMs, boundary + offsetMs);
      const out: NebulizerChartRow[] = [];

      const startOfMonth = dayjs(monthTs);
      const endOfMonth = dayjs(next).subtract(1, "day");
      const isLastMonth =
        next > monthStartTs(domainEndMs) &&
        monthStartTs(domainEndMs) === monthTs;
      const actualEndDay = isLastMonth ? dayjs(domainEndMs) : endOfMonth;
      const monthRangeLabel = `${startOfMonth.format(
        "MMM DD"
      )} - ${actualEndDay.format("DD, YYYY")}`;

      // Complete
      if (agg.cN > 0) {
        const avg = round1(agg.cSum / agg.cN);
        const avgInh = round1(agg.cInhSum / agg.cN);
        const t = base.find((b) => b.monthKey === monthTs)?.original;
        out.push({
          ...(t as IPatientTreatment),
          id: `m-${monthTs}-c`,
          time: new Date(leftPos).toISOString(),
          timeValue: leftPos,
          doseStatus: 1,
          totalTreatmentTime: avg,
          totalTreatmentTimeNum: avg,
          avgInhalingTimeNum: avgInh,
          treatmentsCount: agg.cN,
          latestTreatmentTime: agg.cLatest,
          isAverage: true,
          monthRangeLabel,
        });
      }

      // Incomplete
      if (agg.iN > 0) {
        const avg = round1(agg.iSum / agg.iN);
        const avgInh = round1(agg.iInhSum / agg.iN);
        const t = base.find((b) => b.monthKey === monthTs)?.original;
        out.push({
          ...(t as IPatientTreatment),
          id: `m-${monthTs}-i`,
          time: new Date(rightPos).toISOString(),
          timeValue: rightPos,
          doseStatus: 0,
          totalTreatmentTime: avg,
          totalTreatmentTimeNum: avg,
          avgInhalingTimeNum: avgInh,
          treatmentsCount: agg.iN,
          latestTreatmentTime: agg.iLatest,
          isAverage: true,
          monthRangeLabel,
        });
      }

      return out;
    });

  return rows;
}
